// Per-device online indicator — mirrors the Flutter getStatus():
//   • green dot        → reported within the last 60s (online)
//   • red dot + "Nm"   → offline; N = minutes since it last reported
//
// TWO CLOCKS, WHICH IS THE WHOLE DIFFICULTY HERE.
//
// The server's timestamps are its own local time (IST) labelled UTC, so every
// row reads five and a half hours in the future — see core/devices/deviceStatus.
// This component used to compute `Date.now() - seen` directly, which made that
// difference NEGATIVE and therefore always under the 60-second threshold: EVERY
// DEVICE SHOWED GREEN, permanently, whether or not it was there. A dot that is
// always green is worse than no dot, because it is believed.
//
// So server stamps and live sightings are kept apart and aged separately:
//   • a server stamp goes through ageOf(), which applies the learned offset
//   • a live MQTT message is on OUR clock, and needs no correction at all
// The device is as recent as the better of the two.
//
// ⚠️ NOT EVERY LIST ROW CARRIES A TIMESTAMP, and the ones that do disagree on
// its name:
//
//   room devices     lastUpdatedDateTime   (lowercase l)
//   common devices   LastUpdatedDateTime   (capital L)
//   GetListofDevicesbyUserId   — no time field at all
//
// A tile fed from that last one can never show anything from the row, so this
// falls back to the status endpoint, which always has it. Without that the
// indicator is simply absent, which reads as "the feature is broken".

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { ageOf, ONLINE_WINDOW_MS, getLastSeenCached } from '../../../core/devices/deviceStatus';

function parseTs(raw) {
  if (!raw) return null;
  const t = Date.parse(String(raw).trim().replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

export default function DeviceStatusDot({ device, live }) {
  const raw = device && (device.lastUpdatedDateTime ?? device.LastUpdatedDateTime);
  const serverStampRef = useRef(parseTs(raw));   // server's clock
  const liveAtRef = useRef(null);                // our clock
  const [, force] = useState(0);
  const tick = () => force((n) => n + 1);

  // A fresh row from the server → adopt its timestamp if newer.
  useEffect(() => {
    const t = parseTs(raw);
    if (!t) return;
    if (!serverStampRef.current || t > serverStampRef.current) {
      serverStampRef.current = t;
      tick();
    }
  }, [raw]);

  // The row had no timestamp — ask the status endpoint, which always has one.
  // Cached and shared, so a grid of tiles is one request per device, not one
  // per render.
  const uno = device && (device.UniqueNumber ?? device.uniqueNumber);
  const [fetchedAge, setFetchedAge] = useState(null);
  // When that age was measured, so the "minutes ago" keeps advancing between
  // refetches instead of freezing at whatever it was on arrival.
  const fetchedAtRef = useRef(0);
  useEffect(() => {
    if (raw || !uno) return undefined;
    let alive = true;
    getLastSeenCached(uno).then((age) => {
      if (!alive || age == null) return;
      fetchedAtRef.current = Date.now();
      setFetchedAge(age);
    });
    return () => { alive = false; };
  }, [raw, uno]);

  // A live MQTT message means the device reported just now — no clock skew is
  // involved, because we observed it ourselves.
  useEffect(() => {
    if (live) { liveAtRef.current = Date.now(); tick(); }
  }, [live]);

  // Keep the "minutes ago" fresh.
  useEffect(() => {
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const fromServer = serverStampRef.current != null
    ? ageOf(serverStampRef.current) : Infinity;
  const fromLive = liveAtRef.current != null
    ? Date.now() - liveAtRef.current : Infinity;
  // The fetched age was correct when it arrived; it only grows from there.
  const fromFetch = fetchedAge != null ? fetchedAge + (Date.now() - fetchedAtRef.current) : Infinity;
  const diff = Math.min(fromServer, fromLive, fromFetch);

  if (!Number.isFinite(diff)) return null; // never seen → show nothing

  if (diff < ONLINE_WINDOW_MS) {
    return <FiberManualRecordIcon sx={{ fontSize: 11, color: 'success.main' }} />;
  }
  const mins = Math.floor(diff / 60000);
  return (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
      title={`Last seen ${mins} min ago`}
    >
      <FiberManualRecordIcon sx={{ fontSize: 11, color: 'error.main' }} />
      <Typography variant="caption" color="error.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
        {mins}m
      </Typography>
    </Box>
  );
}
